import { Badge } from "@loot-os/ui/components/badge";
import {
  CircleAlert,
  CircleCheck,
  CircleDashed,
  CircleMinus,
  CircleX,
  type LucideIcon,
} from "lucide-react";

/** Status mirror of one connected account (either mode) — see stripe_accounts. */
export interface StripeAccountStatus {
  chargesEnabled: boolean;
  detailsSubmitted: boolean;
  requirements: {
    currentlyDue: string[];
    pastDue: string[];
    disabledReason: string | null;
  } | null;
}

export type Readiness = "restricted" | "ready" | "action-required" | "pending" | "incomplete";

function deriveReadiness(account: StripeAccountStatus): Readiness {
  const req = account.requirements;
  if (req?.disabledReason || (req?.pastDue.length ?? 0) > 0) return "restricted";
  if (account.chargesEnabled) return "ready";
  if ((req?.currentlyDue.length ?? 0) > 0) return "action-required";
  if (account.detailsSubmitted) return "pending";
  return "incomplete";
}

const READINESS: Record<
  Readiness,
  { label: string; className: string; Icon: LucideIcon; hint: string }
> = {
  ready: {
    label: "Ready to charge",
    className: "border-emerald-500/50 bg-emerald-500/10 text-emerald-400",
    Icon: CircleCheck,
    hint: "Card payments are active on this account.",
  },
  "action-required": {
    label: "Action required",
    className: "border-amber-500/50 bg-amber-500/10 text-amber-400",
    Icon: CircleAlert,
    hint: "Stripe needs more information before charges can be enabled.",
  },
  pending: {
    label: "Pending verification",
    className: "border-sky-500/50 bg-sky-500/10 text-sky-400",
    Icon: CircleDashed,
    hint: "Everything is submitted — Stripe is verifying.",
  },
  restricted: {
    label: "Restricted",
    className: "border-destructive/50 bg-destructive/10 text-destructive",
    Icon: CircleX,
    hint: "Charges are blocked until the issues below are resolved.",
  },
  incomplete: {
    label: "Onboarding incomplete",
    className: "border-border bg-muted/50 text-muted-foreground",
    Icon: CircleDashed,
    hint: "Start (or resume) the hosted onboarding flow to submit KYC.",
  },
};

export function ReadinessBadge({ readiness }: { readiness: Readiness }) {
  const { label, className, Icon } = READINESS[readiness];
  return (
    <Badge variant="outline" className={className}>
      <Icon />
      {label}
    </Badge>
  );
}

function ChecklistRow({
  state,
  label,
  detail,
}: {
  state: "done" | "todo" | "blocked" | "na";
  label: string;
  detail?: string;
}) {
  const Icon =
    state === "done"
      ? CircleCheck
      : state === "blocked"
        ? CircleX
        : state === "na"
          ? CircleMinus
          : CircleDashed;
  const iconClass =
    state === "done"
      ? "text-emerald-400"
      : state === "blocked"
        ? "text-destructive"
        : "text-muted-foreground";
  return (
    <li className="flex items-start gap-2 text-sm">
      <Icon className={`mt-0.5 size-4 shrink-0 ${iconClass}`} />
      <span className={state === "na" ? "text-muted-foreground" : undefined}>
        {label}
        {detail && <span className="text-muted-foreground"> — {detail}</span>}
      </span>
    </li>
  );
}

function RequirementList({
  title,
  items,
  tone,
}: {
  title: string;
  items: string[];
  tone: "destructive" | "warning";
}) {
  if (items.length === 0) return null;
  const box =
    tone === "destructive"
      ? "border-destructive/50 bg-destructive/10 text-destructive"
      : "border-amber-500/50 bg-amber-500/10 text-amber-400";
  return (
    <div className={`rounded-md border px-3 py-2 text-sm ${box}`}>
      <p className="font-medium">
        {title} ({items.length})
      </p>
      <ul className="mt-1 list-disc pl-5 text-xs">
        {items.map((item) => (
          <li key={item}>{item}</li>
        ))}
      </ul>
    </div>
  );
}

/**
 * Readiness panel for one connected account — shared by the Live and Test
 * cards so both modes always read the same way. Overall status is derived, not
 * stored: restricted (disabled or past-due) → ready (charges active) → action
 * required (requirements due) → pending (submitted, Stripe verifying) →
 * incomplete. Payouts are intentionally absent from the checklist as a "to do"
 * — the recipient configuration is never requested, so they can never enable.
 */
export function StripeAccountReadiness({ account }: { account: StripeAccountStatus }) {
  const readiness = deriveReadiness(account);
  const req = account.requirements;

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-2">
        <ReadinessBadge readiness={readiness} />
        <span className="text-xs text-muted-foreground">{READINESS[readiness].hint}</span>
      </div>

      <ul className="flex flex-col gap-1.5">
        <ChecklistRow
          state={account.detailsSubmitted ? "done" : "todo"}
          label="Details submitted"
          detail={account.detailsSubmitted ? undefined : "KYC still outstanding"}
        />
        <ChecklistRow
          state={account.chargesEnabled ? "done" : readiness === "restricted" ? "blocked" : "todo"}
          label="Card payments"
          detail={account.chargesEnabled ? undefined : "not yet enabled"}
        />
        <ChecklistRow
          state="na"
          label="Payouts"
          detail="disabled by design; funds settle to the platform balance"
        />
      </ul>

      {req?.disabledReason && (
        <div className="rounded-md border border-destructive/50 bg-destructive/10 px-3 py-2 text-sm text-destructive">
          Disabled: {req.disabledReason}
        </div>
      )}
      <RequirementList title="Past due" items={req?.pastDue ?? []} tone="destructive" />
      <RequirementList title="Currently due" items={req?.currentlyDue ?? []} tone="warning" />
    </div>
  );
}
